import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Alert,
  Image,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as Clipboard from 'expo-clipboard';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { User, Mail, LogOut, Copy, Check, Camera, Sun, Moon, Monitor, FileText, MessageCircle } from 'lucide-react-native';
import ChavePixSelector from '@/components/ChavePixSelector';
import BannerMenor from '@/components/BannerMenor';
import InstallPWAButton from '@/components/InstallPWAButton';
import { ChavePix } from '@/types/app';
import { router } from 'expo-router';
import { logger } from '@/utils/logger';

export default function ProfileScreen() {
  const { user, signOut, updateProfile, isPremium } = useAuth();
  const { colors, theme, setTheme } = useTheme();
  const [chavePix, setChavePix] = useState<ChavePix | undefined>(user?.chavePix);
  const [pixCopiado, setPixCopiado] = useState(false);

  // Estados para alertas nativos
  const [showNativeAlert, setShowNativeAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    onConfirm?: () => void;
    showCancel?: boolean;
  }>({ title: '', message: '' });

  // Função de alert que funciona no web e mobile
  const showAlert = (title: string, message: string, onConfirm?: () => void, showCancel: boolean = true) => {
    setAlertConfig({ title, message, onConfirm, showCancel });
    setShowNativeAlert(true);
  };

  const handleSignOut = () => {
    showAlert(
      'Sair da conta',
      'Tem certeza que deseja sair?',
      () => {
        logger.debug('Confirming logout');
        signOut();
      }
    );
  };



  const copiarChavePix = async () => {
    if (chavePix?.valor) {
      try {
        await Clipboard.setStringAsync(chavePix.valor);
        setPixCopiado(true);
        setTimeout(() => setPixCopiado(false), 2000);
        Alert.alert('Copiado!', `Chave PIX copiada para a área de transferência`);
      } catch {
        Alert.alert('Erro', 'Falha ao copiar chave PIX');
      }
    } else {
      Alert.alert('Aviso', 'Nenhuma chave PIX configurada');
    }
  };

  const alterarFoto = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (status !== 'granted') {
      Alert.alert('Permissão necessária', 'Precisamos de acesso à galeria para alterar sua foto.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      try {
        await updateProfile({ fotoURL: result.assets[0].uri });
        Alert.alert('Sucesso', 'Foto atualizada!');
      } catch {
        Alert.alert('Erro', 'Falha ao atualizar foto');
      }
    }
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light': return Sun;
      case 'dark': return Moon;
      default: return Monitor;
    }
  };

  const getThemeText = () => {
    switch (theme) {
      case 'light': return 'Claro';
      case 'dark': return 'Escuro';
      default: return 'Sistema';
    }
  };

  const alternarTema = () => {
    const proximoTema = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(proximoTema);
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 22,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    content: {
      flex: 1,
      paddingHorizontal: 24,
    },
    profileSection: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 24,
      marginTop: 16,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
    },
    avatarContainer: {
      marginBottom: 16,
      position: 'relative',
    },
    avatar: {
      width: 80,
      height: 80,
      borderRadius: 40,
    },
    avatarPlaceholder: {
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    cameraButton: {
      position: 'absolute',
      bottom: -4,
      right: -4,
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
      borderColor: colors.surface,
    },
    userInfo: {
      alignItems: 'center',
    },
    userNameContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    userName: {
      fontSize: 20,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 4,
    },
    premiumBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    premiumText: {
      fontSize: 12,
      fontFamily: 'Inter-SemiBold',
      color: colors.warning,
    },
    userEmail: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    emailVerificationBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.success + '20',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      marginTop: 8,
      gap: 4,
    },
    emailVerificationText: {
      fontSize: 12,
      fontFamily: 'Inter-Medium',
      color: colors.success,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      marginTop: 16,
      borderWidth: 1,
      borderColor: colors.border,
    },
    sectionTitle: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 4,
    },
    sectionSubtitle: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginBottom: 16,
      lineHeight: 16,
    },
    pixContainer: {
      marginTop: 8,
    },
    pixEditContainer: {
      gap: 12,
    },
    pixInput: {
      backgroundColor: colors.background,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 48,
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      borderWidth: 1,
      borderColor: colors.border,
    },
    pixEditButtons: {
      flexDirection: 'row',
      gap: 12,
    },
    pixCancelButton: {
      flex: 1,
      backgroundColor: colors.border,
      borderRadius: 12,
      paddingVertical: 12,
      alignItems: 'center',
    },
    pixCancelButtonText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.textSecondary,
    },
    pixSaveButton: {
      flex: 1,
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 12,
      alignItems: 'center',
    },
    pixSaveButtonText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: '#FFFFFF',
    },
    pixDisplayContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    pixInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      gap: 12,
    },
    pixText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      flex: 1,
    },
    pixActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    pixCopyButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    pixEditButton: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: colors.primary + '20',
    },
    pixEditButtonText: {
      fontSize: 12,
      fontFamily: 'Inter-Medium',
      color: colors.primary,
    },
    infoItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      gap: 16,
    },
    infoIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    infoContent: {
      flex: 1,
    },
    infoLabel: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginBottom: 2,
    },
    infoValue: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    themeButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingVertical: 16,
      paddingHorizontal: 16,
      borderWidth: 1,
      borderColor: colors.border,
    },
    themeButtonContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    themeButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    themeValue: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    infoLinkButton: {
      marginBottom: 8,
    },
    infoLinkContent: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      gap: 16,
    },
    infoLinkText: {
      fontSize: 16,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    signOutButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.error + '20',
      borderRadius: 12,
      paddingVertical: 16,
      gap: 8,
      borderWidth: 1,
      borderColor: colors.error + '40',
    },
    signOutButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-Medium',
      color: colors.error,
    },
    alertOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      elevation: 9999,
    },
    alertContainer: {
      backgroundColor: colors.surface,
      padding: 24,
      borderRadius: 16,
      width: '80%',
      maxWidth: 400,
    },
    alertTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    alertMessage: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginBottom: 24,
    },
    alertButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    alertButton: {
      padding: 12,
      borderRadius: 8,
      backgroundColor: colors.primary + '20',
    },
    alertButtonCancel: {
      backgroundColor: colors.error + '20',
    },
    alertButtonCancelText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.error,
    },
    alertButtonConfirm: {
      backgroundColor: colors.success + '20',
    },
    alertButtonConfirmText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.success,
    },

  });

  const ThemeIcon = getThemeIcon();

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Perfil</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <BannerMenor />

        <View style={styles.profileSection}>
          <View style={styles.avatarContainer}>
            {user?.fotoURL ? (
              <Image source={{ uri: user.fotoURL }} style={styles.avatar} />
            ) : (
              <View style={styles.avatarPlaceholder}>
                <User size={40} color={colors.textSecondary} />
              </View>
            )}
            <TouchableOpacity style={styles.cameraButton} onPress={alterarFoto}>
              <Camera size={16} color="#FFFFFF" />
            </TouchableOpacity>
          </View>

          <View style={styles.userInfo}>
            <View style={styles.userNameContainer}>
              <Text style={styles.userName}>{user?.nome}</Text>
              {isPremium() && (
                <View style={styles.premiumBadge}>
                  <Image source={require('@/assets/images/crown.png')} style={{ width: 16, height: 16, backgroundColor: 'transparent', borderRadius: 8 }} resizeMode="contain" />
                  <Text style={styles.premiumText}>Premium</Text>
                </View>
              )}
            </View>
            <Text style={styles.userEmail}>{user?.email}</Text>
            {user?.emailVerificado && (
              <View style={styles.emailVerificationBadge}>
                <Check size={12} color={colors.success} />
                <Text style={styles.emailVerificationText}>Email verificado</Text>
              </View>
            )}


          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Tema do Aplicativo</Text>
          <TouchableOpacity style={styles.themeButton} onPress={alternarTema}>
            <View style={styles.themeButtonContent}>
              <ThemeIcon size={20} color={colors.text} />
              <Text style={styles.themeButtonText}>Aparência</Text>
            </View>
            <Text style={styles.themeValue}>{getThemeText()}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Chave PIX</Text>
          <Text style={styles.sectionSubtitle}>
            Esta chave será usada por padrão quando outros precisarem te pagar
          </Text>

          <View style={styles.pixContainer}>
            <ChavePixSelector
              value={chavePix}
              onChange={async (novaChavePix) => {
                setChavePix(novaChavePix);
                try {
                  // Salvar sempre, seja para adicionar ou remover a chave PIX
                  await updateProfile({ chavePix: novaChavePix || null });
                  Alert.alert('Sucesso', novaChavePix ? 'Chave PIX atualizada!' : 'Chave PIX removida!');
                } catch {
                  Alert.alert('Erro', 'Falha ao atualizar chave PIX');
                }
              }}
              placeholder="Configure sua chave PIX"
            />

            {chavePix && (
              <TouchableOpacity
                style={styles.pixCopyButton}
                onPress={copiarChavePix}
              >
                {pixCopiado ? (
                  <Check size={16} color={colors.success} />
                ) : (
                  <Copy size={16} color={colors.primary} />
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informações da Conta</Text>

          <View style={styles.infoItem}>
            <View style={styles.infoIcon}>
              <User size={20} color={colors.textSecondary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>Nome</Text>
              <Text style={styles.infoValue}>{user?.nome || ''}</Text>
            </View>
          </View>

          <View style={styles.infoItem}>
            <View style={styles.infoIcon}>
              <Mail size={20} color={colors.textSecondary} />
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoLabel}>Email</Text>
              <Text style={styles.infoValue}>{user?.email || ''}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informações</Text>

          <TouchableOpacity
            style={styles.infoLinkButton}
            onPress={() => router.push('/politica-privacidade')}
          >
            <View style={styles.infoLinkContent}>
              <View style={styles.infoIcon}>
                <FileText size={20} color={colors.textSecondary} />
              </View>
              <Text style={styles.infoLinkText}>Política de Privacidade</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.infoLinkButton}
            onPress={() => router.push('/termos-de-uso')}
          >
            <View style={styles.infoLinkContent}>
              <View style={styles.infoIcon}>
                <FileText size={20} color={colors.textSecondary} />
              </View>
              <Text style={styles.infoLinkText}>Termos de Uso</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.infoLinkButton}
            onPress={() => router.push('/contato')}
          >
            <View style={styles.infoLinkContent}>
              <View style={styles.infoIcon}>
                <MessageCircle size={20} color={colors.textSecondary} />
              </View>
              <Text style={styles.infoLinkText}>Contato</Text>
            </View>
          </TouchableOpacity>

          <InstallPWAButton />
        </View>

        <View style={styles.section}>
          <TouchableOpacity style={styles.signOutButton} onPress={handleSignOut}>
            <LogOut size={20} color={colors.error} />
            <Text style={styles.signOutButtonText}>Sair da Conta</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {showNativeAlert && (
        <View style={styles.alertOverlay}>
          <View style={styles.alertContainer}>
            <Text style={styles.alertTitle}>{alertConfig.title}</Text>
            <Text style={styles.alertMessage}>{alertConfig.message}</Text>
            <View style={styles.alertButtons}>
              {alertConfig.showCancel && (
                <TouchableOpacity
                  style={styles.alertButtonCancel}
                  onPress={() => {
                    setShowNativeAlert(false);
                  }}
                >
                  <Text style={styles.alertButtonCancelText}>Cancelar</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.alertButtonConfirm}
                onPress={() => {
                  setShowNativeAlert(false);
                  alertConfig.onConfirm?.();
                }}
              >
                <Text style={styles.alertButtonConfirmText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
}